import React from "react";
import { Play, Volume2 } from "lucide-react";
import { GLOWS, DEFAULT_LOGO } from "./LogoRevealBg";
import { splashMedia } from "./WelcomeSplash";

const Toggle = ({ on, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative h-6 w-11 shrink-0 rounded-full transition ${on ? "bg-blue-600" : "bg-slate-300"}`}
    data-testid={testId}
  >
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

const Choice = ({ on, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center justify-center gap-2 rounded-lg border-2 px-2 py-2 text-xs font-semibold transition ${
      on ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-blue-300"
    }`}
    data-testid={testId}
  >
    {children}
  </button>
);

// Panel del editor: pantalla de bienvenida (logo/foto del cliente en 3D, color, sonido, vista previa).
const SplashSettings = ({ draft, setDraft, setBackground, onPreview }) => {
  const enabled = draft.splash !== false;
  const mode = draft.splashLogo || "client";
  const media = splashMedia(draft);
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  return (
    <div className="space-y-4" data-testid="splash-settings">
      <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 p-3">
        <div>
          <p className="text-sm font-semibold text-slate-700">Mostrar al abrir el portafolio</p>
          <p className="text-[11px] text-slate-400">2 segundos en 3D con destello y luego se transforma en el portafolio.</p>
        </div>
        <Toggle on={enabled} onClick={() => set({ splash: !enabled })} testId="splash-toggle" />
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Qué se muestra</p>
        <div className="grid grid-cols-3 gap-2">
          <Choice on={mode === "client"} onClick={() => set({ splashLogo: "client" })} testId="splash-src-client">Foto del cliente</Choice>
          <Choice on={mode === "custom"} onClick={() => set({ splashLogo: "custom" })} testId="splash-src-custom">Logo propio</Choice>
          <Choice on={mode === "uaicard"} onClick={() => set({ splashLogo: "uaicard" })} testId="splash-src-uaicard">UAICARD</Choice>
        </div>
        <div className="flex items-center gap-3 rounded-xl bg-black p-3">
          {media.photo ? (
            <img src={media.photo} alt="" className="h-14 w-14 rounded-full object-cover ring-2 ring-white/70" />
          ) : (
            <img src={media.logo || DEFAULT_LOGO} alt="" className="h-14 w-20 object-contain" />
          )}
          <p className="text-[11px] leading-snug text-white/60">
            {mode === "client" && !media.photo
              ? "Este portafolio no tiene foto de perfil; se usará el logo."
              : mode === "custom" && !draft.background?.logo
              ? "Sube tu logo en Cambiar fondo → Logo 3D. Mientras, se usa UAICARD."
              : "Así se verá en la entrada (en 3D con destello)."}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Color del destello</p>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(GLOWS).map(([k, g]) => (
            <Choice key={k} on={(draft.background?.glow || "blue") === k} onClick={() => setBackground({ glow: k })} testId={`splash-glow-${k}`}>
              <span className="h-4 w-4 rounded-full border border-slate-300" style={{ background: g.swatch, boxShadow: `0 0 8px ${g.swatch}` }} />
              {g.label}
            </Choice>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 p-3">
        <div className="flex items-start gap-2">
          <Volume2 className="mt-0.5 h-4 w-4 text-slate-500" />
          <div>
            <p className="text-sm font-semibold text-slate-700">Sonido "whoosh"</p>
            <p className="text-[11px] text-slate-400">Suave, al transformarse. Algunos teléfonos lo silencian si aún no se ha tocado la pantalla.</p>
          </div>
        </div>
        <Toggle on={!!draft.splashSound} onClick={() => set({ splashSound: !draft.splashSound })} testId="splash-sound-toggle" />
      </div>

      <button
        type="button"
        onClick={onPreview}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-bold text-white transition hover:bg-blue-700"
        data-testid="splash-preview-btn"
      >
        <Play className="h-4 w-4" /> Ver vista previa
      </button>
    </div>
  );
};

export default SplashSettings;
